import "../index.scss";
import "../styles/ResourceLandingPage.scss";
import { getCertificationsList } from "../services/certification";
import { getCoursesAndFirstLessons } from "../services/course";
import { getLearningPaths } from "../services/learning-path";
import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ClayLayout from "@clayui/layout";
import EducationResourceEmptyResults from "./Common/EducationResourceEmptyResult";
import Faq from "./Certification/Faq";
import Filter from "./Common/Filter";
import LandingPageCard from "./Common/LandingPageCard";
import Pagination from "./Common/Pagination";
import SearchBar from "./Common/SearchBar";
import useFetchExpertiseLevel from "../Hooks/useFetchExpertiseLevel";
import useFetchPersonas from "../Hooks/useFetchPersonas";
import useFetchUserEnrollments from "../Hooks/useFetchUserEnrollments";

const ResourceLandingPage = ({ type }) => {
  const [completedCourses, setCompletedCourses] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
	const [educationResource, setEducationResource] = useState([]);
  const [hideCompletedCourses, setHideCompletedCourses] = useState(false);
	const [pageitems, setPageitems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedExpertiseLevels, setSelectedExpertiseLevels] = useState([]);
  const [selectedPersonas, setSelectedPersonas] = useState([]);

  const { expertiseLevels } = useFetchExpertiseLevel();
  const { personas } = useFetchPersonas();
  const { t } = useTranslation();
  const { userCompleteEnrollments, userInProgressEnrollments } = useFetchUserEnrollments();

  const indexOfLast = currentPage * pageitems;

  const indexOfFirst = indexOfLast - pageitems;

  const fetchEducationResourceByType = {
    course: async () => {
      const data = await getCoursesAndFirstLessons();

      return data
        .filter((course) => course.lesson && course.r_module_c_course)
        .sort(
          (a, b) => a.r_module_c_course.position - b.r_module_c_course.position
        )
        .map((course) => ({
          ...course.r_module_c_course,
          firstLesson: course.lesson[0]?.id,
        }))
        .filter((course) => course.firstLesson);
      },

      certification: async () => {
        return await getCertificationsList();
      },

      learningPath: async () => {
        return await getLearningPaths(-1);
      }
  };

  const filteredEducationResources = useMemo(() => {
    return educationResource?.filter((item) => {
      const level = item.level?.key?.toLowerCase() || "";
      const personas = item.audience?.map((aud) => aud.name.toLowerCase()) || [];
      const title = (item.title || item.name || "").toLowerCase();

      const matchesCompletedCourses =
        !hideCompletedCourses || !completedCourses.includes(item.id);

      const matchesLevel =
        selectedExpertiseLevels.length === 0 ||
        selectedExpertiseLevels.map((l) => l.toLowerCase()).includes(level);

      const matchesPersonas =
        selectedPersonas.length === 0 ||
        personas.some((persona) =>
          selectedPersonas.map((p) => p.toLowerCase()).includes(persona)
        );

      const matchesTitle = title.includes(searchTerm.toLowerCase());  

      return matchesCompletedCourses && matchesLevel && matchesPersonas && matchesTitle ;
    });
  }, [
    completedCourses,
    educationResource,
    hideCompletedCourses,
    searchTerm,
    selectedExpertiseLevels,
    selectedPersonas,
  ])

  const paginatedEducationResource = filteredEducationResources.slice(indexOfFirst, indexOfLast);

  useEffect(() => {
    const fetchData = async () => {
      if (fetchEducationResourceByType[type]) {
        const data = await fetchEducationResourceByType[type]();

        setEducationResource(data);
      }
    };
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  return (
    <>
      {type === "course" && (
        <SearchBar
          educationResourceItems={educationResource || []}
          onSearch={(term) => setSearchTerm(term)}
          type={type}
        />  
      )}
      <div className="landing-page-main-container">
        {type === "course" && (
          <>
            <div className="filter-container">
              <Filter
                completedCoursesLabel={t("hide-completed-courses")}
                hideCompletedCourses={hideCompletedCourses}
                isCompletedCourses
                setHideCompletedCourses={setHideCompletedCourses}
                title={t("course-progress")}
              />
              <Filter
                items={personas}
                onChange={setSelectedPersonas}
                selectedItems={selectedPersonas}
                title={t("filter-by-personas")}
              />
              <Filter
                items={expertiseLevels}
                onChange={setSelectedExpertiseLevels}
                selectedItems={selectedExpertiseLevels}
                title={t("levels")}
              />
            </div>
          </>
        )}
        <div className="landing-page-cards-container">
          <div className="landing-page-cards-list">
            {paginatedEducationResource?.length > 0 ?
              paginatedEducationResource.map((educationResourceItem, index) => (
                <ClayLayout.Col className="card-layout" key={index}>
                  <LandingPageCard 
                    educationResourceItem={educationResourceItem}
                    setCompletedCourses={setCompletedCourses}
                    t={t}
                    type={type}
                    userCompleteEnrollments={userCompleteEnrollments}
                    userInProgressEnrollments={userInProgressEnrollments}
                  />
                </ClayLayout.Col>
              )): (
                <EducationResourceEmptyResults searchTerm={searchTerm} t={t}/>
            )}
          </div>
          {type === "certification" && <Faq />}
          <Pagination
            currentPage={currentPage}
            pageitems={pageitems}
            setCurrentPage={setCurrentPage}
            setPageitems={setPageitems}
            totalItems={filteredEducationResources?.length}
          />
        </div>
      </div>
    </>
  );
};

export default ResourceLandingPage;